import type { RouteRecordNormalized } from 'vue-router'
import { useRootStore } from '~/stores/root'
import { UserRole } from '~/types/account'
import { PORTAL_ROOT } from '~/utils/portalRoutes'

type RoleMeta = { meta?: { disallowedRoles?: UserRole[] | UserRole } }

// Accumulate `disallowedRoles` across every matched record, and normalize a single value to an
// array -- the same reduce nuxt2 does over `route.meta`. Reading only `to.meta.meta` (as this
// file used to) relies on vue-router's SHALLOW merge across matched records, so a child that
// declares its own `meta` key replaces the parent's wholesale rather than adding to it. Today
// every child under /account happens to declare a superset of the parent's set, so nothing is
// mis-guarded -- but the first child that omits a role the parent blocks would silently grant
// that role access, with nothing to catch it. (AB#63056)
const collectDisallowedRoles = (matched: RouteRecordNormalized[]): UserRole[] =>
  matched.reduce<UserRole[]>((accumulator, record) => {
    const roles = (record.meta as RoleMeta)?.meta?.disallowedRoles ?? []
    return accumulator.concat(Array.isArray(roles) ? roles : [roles])
  }, [])

// This guard deliberately does NOT fall back to the httpOnly `userRole` cookie. It used to, and
// that was a fail-open authorization path (AB#63056):
//
//   * nuxt2's equivalent consults the cookie only when the auth module produced no user at all,
//     and carries an explicit warning against doing it once auth has DETERMINED Anonymous --
//     "overriding with a stale userRole cookie would allow a logged-out customer (who received a
//     fresh anonymous session) to pass role checks". This file gated on the role already being
//     Anonymous, i.e. precisely the case that warning names.
//   * That "auth produced no user" state does not exist here. `init-auth.server.ts` is
//     `enforce: 'pre'` and AWAITS `fetchSession()`, which sets `data`/`loggedIn` on both its
//     success and failure paths, so by the time any route middleware runs on SSR the role is
//     already decided. The only case the fallback still covered was `fetchSession` failing, and
//     trusting a stale role cookie during a session-API outage is fail-open.
//   * It bought very little even then: this guard controls ROUTING only. The page's own requests
//     still carry `auth_cookie` and will 401, so the fallback's effect was to show a page shell
//     that cannot load its data instead of redirecting cleanly.
//
// Trade-off, deliberate: during a session-API outage a demonstrator is now redirected rather than
// silently continuing. Failing closed on an authorization control is the intended behaviour.
//
// Removing it also deletes the only `import.meta.server` branch in this file, so every path below
// is reachable under vitest's `environment: 'nuxt'` (which inlines that flag to `false`) and the
// spec can cover the guard completely -- no dependency on AB#63036.
export default defineNuxtRouteMiddleware((to, _from) => {
  const rootStore = useRootStore()

  const disallowedRoles = collectDisallowedRoles(to.matched)

  // Resolved into a local, as nuxt2 does. This guard used to call setUserRole(), persisting a
  // role into app-wide auth state as a side effect of a route check. A guard decides; it does
  // not mutate. The defensive default matches nuxt2's catch and recruit-guard's shape.
  let effectiveRole: UserRole
  try {
    effectiveRole = useSAuth().userRole.value
  } catch (_e) {
    effectiveRole = UserRole.Anonymous
  }

  if (!disallowedRoles.includes(effectiveRole)) {
    return
  }

  if ((to.path.includes(PORTAL_ROOT) || to.path.includes('/agreements')) && effectiveRole !== UserRole.Demonstrator) {
    const redirectUrl = rootStore.isActiveDemo
      ? '/help/page-unavailable-customer-view'
      : effectiveRole === UserRole.Customer
        ? '/help/demonstrators-only'
        : '/help/unauthorized'
    return navigateTo(`${redirectUrl}?redirect=${to.fullPath}`)
  }
  if (to.path.includes('/account')) {
    return navigateTo(`/help/unauthorized?redirect=${to.fullPath}`)
  }
  return effectiveRole !== UserRole.Demonstrator ? navigateTo('/') : navigateTo(PORTAL_ROOT)
})
